import { useSearchParams } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { MaterialCombobox } from "@/components/MaterialRequestPanel";
import NotificationsPanel from "@/components/NotificationsPanel";
import HistoryStamps from "@/components/HistoryStamps";
import { fmtDay } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Warehouse, Upload, Download, Plus, Pencil, PackagePlus, ArrowRightLeft, ClipboardList,
  Search, IndianRupee, Loader2, Check, X, Trash2, AlertTriangle, Bell, RotateCcw, Printer, ScanLine,
} from "lucide-react";
import { printMaterialList, requestEntry, transferEntry, requestCode } from "@/components/PrintMaterialList";
import { toast } from "sonner";

const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const LOW_STOCK = 10; // eslint-disable-line no-unused-vars

export default function StoreView() {
  const { user } = useAuth();
  const fixed = user?.location && ["MIHAN", "MANEWADA"].includes(user.location);
  const [store, setStore] = useState(fixed ? user.location : "MIHAN");
  const [params, setParams] = useSearchParams();
  const issueId = params.get("issue");
  const [tab, setTab] = useState(issueId ? "requests" : "inventory");
  const clearIssue = () => setParams({}, { replace: true });

  useEffect(() => {
    if (!issueId || fixed) return;
    api.get("/material-requests").then((r) => {
      const found = r.data.find((x) => x.id === issueId);
      if (found) setStore(found.store);
    }).catch(() => {});
  }, [issueId, fixed]);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <Warehouse className="h-5 w-5 text-primary" />
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Store — {store}</h1>
            </div>
            <p className="text-muted-foreground">Raw material inventory, issuing &amp; transfers.</p>
          </div>
          {!fixed && (
            <div className="flex gap-2">
              {["MIHAN", "MANEWADA"].map((s) => (
                <Button key={s} variant={store === s ? "default" : "outline"} onClick={() => setStore(s)} data-testid={`store-filter-${s.toLowerCase()}`}>{s}</Button>
              ))}
            </div>
          )}
          {fixed && <Badge className="h-9 px-4 text-sm">{store} Store-keeper</Badge>}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="inventory" data-testid="store-tab-inventory" className="gap-2"><ClipboardList className="h-4 w-4" /> Inventory</TabsTrigger>
            <TabsTrigger value="requests" data-testid="store-tab-requests" className="gap-2"><PackagePlus className="h-4 w-4" /> Requests</TabsTrigger>
            <TabsTrigger value="transfers" data-testid="store-tab-transfers" className="gap-2"><ArrowRightLeft className="h-4 w-4" /> Transfers</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="store-tab-notifications" className="gap-2"><Bell className="h-4 w-4" /> Notifications</TabsTrigger>
          </TabsList>
          <TabsContent value="inventory" className="mt-6"><Inventory store={store} /></TabsContent>
          <TabsContent value="requests" className="mt-6"><Requests store={store} issueId={issueId} onIssueHandled={clearIssue} /></TabsContent>
          <TabsContent value="transfers" className="mt-6"><Transfers store={store} fixed={fixed} /></TabsContent>
          <TabsContent value="notifications" className="mt-6"><NotificationsPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

/* ---------------- Inventory ---------------- */
const emptyMat = { name: "", category: "General", unit: "Kg", rate: "", stock: "", reorder_level: "10" };
function Inventory({ store }) {
  const [mats, setMats] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyMat);
  const [stockDlg, setStockDlg] = useState(null);
  const [stockQty, setStockQty] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/materials", { params: { store } }).then((r) => setMats(r.data)).catch(() => {});
  useEffect(() => { load(); }, [store]);

  const filtered = useMemo(
    () => mats.filter((m) => m.name.toLowerCase().includes(q.toLowerCase()) || (m.category || "").toLowerCase().includes(q.toLowerCase())),
    [mats, q]
  );
  const totalValue = useMemo(() => mats.reduce((s, m) => s + (m.stock || 0) * (m.rate || 0), 0), [mats]);
  const lowItems = useMemo(() => mats.filter((m) => (m.stock || 0) <= (m.reorder_level ?? 10)), [mats]);

  const openAdd = () => { setEditing(null); setForm(emptyMat); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ ...m, rate: String(m.rate), stock: String(m.stock), reorder_level: String(m.reorder_level ?? 10) }); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      if (editing) {
        await api.put(`/materials/${editing.id}`, { name: form.name, category: form.category, unit: form.unit, rate: Number(form.rate), stock: Number(form.stock), reorder_level: Number(form.reorder_level || 0) });
      } else {
        await api.post("/materials", { name: form.name, category: form.category, unit: form.unit, rate: Number(form.rate || 0), stock: Number(form.stock || 0), reorder_level: Number(form.reorder_level || 0), store });
      }
      toast.success("Material saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doStockIn = async () => {
    try {
      await api.patch(`/materials/${stockDlg.id}/stock-in`, { qty: Number(stockQty) });
      toast.success(`Added ${stockQty} ${stockDlg.unit} to ${stockDlg.name}`);
      setStockDlg(null); setStockQty(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/materials/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadTemplate = () => {
    const header = "item_name,category,unit,rate,opening_stock,reorder_level,store";
    const sample = [
      `Tomato,Vegetables,Kg,40,120,30,${store}`,
      `Refined Oil,Oils,Ltr,140,90,25,${store}`,
      `Paper Plates,Disposables,Pcs,2,2000,500,${store}`,
    ];
    const csv = [header, ...sample].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `baron_inventory_template_${store}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Template downloaded");
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/materials/upload", fd, { params: { store }, headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.created} new, ${data.updated} updated`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <Card className="p-5 border-orange-100 dark:border-slate-800">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h3 className="font-heading font-bold text-lg">Inventory ({mats.length} items)</h3>
          <p className="text-sm text-muted-foreground">Total stock value: <span className="font-mono font-semibold text-primary">{fmtMoney(totalValue)}</span></p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onUpload} data-testid="store-upload-input" />
          <Button variant="ghost" className="gap-2" onClick={downloadTemplate} data-testid="store-download-template-button">
            <Download className="h-4 w-4" /> Template
          </Button>
          <Button variant="outline" className="gap-2" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="store-upload-button">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload CSV/Excel
          </Button>
          <Button className="gap-2" onClick={openAdd} data-testid="store-add-material-button"><Plus className="h-4 w-4" /> Add Material</Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input className="pl-9" placeholder="Search material or category..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="store-search-input" />
      </div>

      {lowItems.length > 0 && (
        <div data-testid="store-low-stock-banner" className="mb-4 rounded-xl border border-rose-300 bg-rose-50 dark:bg-rose-950/40 dark:border-rose-900 p-4 fade-up">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-rose-600 pulse-dot" />
            <p className="font-heading font-bold text-rose-700 dark:text-rose-300" data-testid="store-low-stock-count">
              {lowItems.length} material{lowItems.length > 1 ? "s" : ""} at or below reorder level
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowItems.slice(0, 12).map((m) => (
              <Badge key={m.id} variant="outline" className="border-rose-300 text-rose-700 dark:text-rose-300 bg-white/60 dark:bg-transparent" data-testid="store-low-stock-chip">
                {m.name}: {m.stock} {m.unit} <span className="opacity-60 ml-1">/ reorder {m.reorder_level ?? 10}</span>
              </Badge>
            ))}
            {lowItems.length > 12 && <span className="text-sm text-rose-700 dark:text-rose-300 self-center">+{lowItems.length - 12} more</span>}
          </div>
        </div>
      )}

      <div className="max-h-[560px] overflow-auto rounded-lg border border-border">
        <Table>
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead>Material</TableHead><TableHead>Category</TableHead><TableHead>Unit</TableHead>
              <TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Stock</TableHead>
              <TableHead className="text-right">Reorder</TableHead>
              <TableHead className="text-right">Value</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No materials. Upload a CSV or add manually.</TableCell></TableRow>
            )}
            {filtered.map((m) => (
              <TableRow key={m.id} data-testid="store-material-row">
                <TableCell className="font-medium">{m.name}</TableCell>
                <TableCell className="text-muted-foreground">{m.category}</TableCell>
                <TableCell><Badge variant="secondary">{m.unit}</Badge></TableCell>
                <TableCell className="text-right font-mono">{fmtMoney(m.rate)}</TableCell>
                <TableCell className="text-right font-mono">
                  <span className={m.stock <= (m.reorder_level ?? 10) ? "text-rose-600 font-semibold" : ""}>{m.stock}</span>
                  {m.stock <= (m.reorder_level ?? 10) && <Badge variant="outline" className="ml-2 border-rose-300 text-rose-600 text-[10px]">Low</Badge>}
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{m.reorder_level ?? 10}</TableCell>
                <TableCell className="text-right font-mono">{fmtMoney((m.stock || 0) * (m.rate || 0))}</TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon" title="Stock in" onClick={() => { setStockDlg(m); setStockQty(""); }} data-testid="store-stockin-button"><PackagePlus className="h-4 w-4 text-emerald-600" /></Button>
                  <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(m)} data-testid="store-edit-material-button"><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" title="Delete" onClick={() => del(m.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Material</DialogTitle>
            <DialogDescription>Material for the {store} store.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name</Label><Input data-testid="store-material-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-2"><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Unit</Label>
                <Select value={form.unit} onValueChange={(v) => setForm({ ...form, unit: v })}>
                  <SelectTrigger data-testid="store-material-unit-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Kg", "Pcs", "Ltr", "Gm", "Pkt", "Box"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>Rate (₹)</Label><Input type="number" data-testid="store-material-rate-input" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></div>
              <div className="space-y-2"><Label>Stock</Label><Input type="number" data-testid="store-material-stock-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} /></div>
              <div className="space-y-2"><Label>Reorder Level</Label><Input type="number" data-testid="store-material-reorder-input" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} data-testid="store-material-submit-button">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Stock-in dialog */}
      <Dialog open={!!stockDlg} onOpenChange={(v) => !v && setStockDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Add Stock — {stockDlg?.name}</DialogTitle>
            <DialogDescription>Record purchased material arriving in store. Current: {stockDlg?.stock} {stockDlg?.unit}.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Quantity to add ({stockDlg?.unit})</Label>
            <Input type="number" autoFocus value={stockQty} onChange={(e) => setStockQty(e.target.value)} data-testid="store-stockin-qty-input" />
          </div>
          <DialogFooter><Button onClick={doStockIn} data-testid="store-stockin-submit-button" className="gap-2"><PackagePlus className="h-4 w-4" /> Add Stock</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

/* ---------------- Requests (store side: issue/reject) ---------------- */
function Requests({ store, issueId, onIssueHandled }) {
  const [reqs, setReqs] = useState([]);
  const [mats, setMats] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [highlightId, setHighlightId] = useState(null);
  const [scanCode, setScanCode] = useState("");
  const [issueReq, setIssueReq] = useState(null);
  const [issueQty, setIssueQty] = useState({});
  const [busy, setBusy] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");

  const load = () => api.get("/material-requests", { params: { store } }).then((r) => setReqs(r.data)).catch(() => {});
  const loadMats = () => api.get("/materials", { params: { store } }).then((r) => setMats(r.data)).catch(() => {});
  useEffect(() => {
    setLoaded(false);
    Promise.all([load(), loadMats()]).then(() => setLoaded(true));
    const t = setInterval(load, 12000);
    return () => clearInterval(t);
  }, [store]);

  const stockOf = (material_id) => {
    const m = mats.find((x) => x.id === material_id);
    return m ? m.stock : 0;
  };

  const openIssue = (r) => {
    setIssueReq(r);
    setIssueQty(Object.fromEntries(r.items.map((it) => [it.material_id, Math.min(it.qty, stockOf(it.material_id))])));
  };

  const openByCode = (raw) => {
    const val = (raw || "").trim();
    if (!val) return;
    const code = (val.match(/issue=([\w-]+)/)?.[1] || val.replace(/^#/, "")).toUpperCase();
    const r = reqs.find((x) => x.id.toUpperCase() === code || x.id.toUpperCase().startsWith(code));
    if (!r) return toast.error(`No request #${code.slice(0, 8)} found for ${store} Store`);
    setHighlightId(r.id);
    setTimeout(() => document.getElementById(`req-${r.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
    if (r.status === "Pending") openIssue(r);
    else toast.info(`Request #${requestCode(r.id)} is already ${r.status}`);
  };

  useEffect(() => {
    if (!issueId || !loaded) return;
    openByCode(issueId);
    onIssueHandled();
  }, [issueId, loaded]); // eslint-disable-line

  const confirmIssue = async () => {
    setBusy(true);
    try {
      await api.patch(`/material-requests/${issueReq.id}/issue`, {
        items: issueReq.items.map((it) => ({ material_id: it.material_id, qty: Number(issueQty[it.material_id] || 0) })),
      });
      toast.success("Issued — stock deducted");
      setIssueReq(null);
      load(); loadMats();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reject = async (id) => {
    try {
      await api.patch(`/material-requests/${id}/reject`);
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const statusCfg = {
    Pending: "bg-amber-100 text-amber-800 border-amber-300",
    Issued: "bg-blue-100 text-blue-800 border-blue-300",
    Received: "bg-emerald-100 text-emerald-800 border-emerald-300",
    Rejected: "bg-rose-100 text-rose-800 border-rose-300",
  };

  const filtered = useMemo(() => reqs.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (dateFrom && new Date(r.created_at) < new Date(dateFrom)) return false;
    return true;
  }), [reqs, statusFilter, dateFrom]);

  const totals = useMemo(() => {
    let issued = 0, received = 0;
    for (const r of filtered) for (const it of r.items) {
      issued += (it.issued_qty || 0) * (it.rate || 0);
      received += (it.received_qty || 0) * (it.rate || 0);
    }
    return { issued, received };
  }, [filtered]);

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => { e.preventDefault(); openByCode(scanCode); setScanCode(""); }}
        >
          <div className="relative">
            <ScanLine className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={scanCode}
              onChange={(e) => setScanCode(e.target.value)}
              placeholder="Scan or type #"
              className="w-56 pl-8 font-mono"
              data-testid="store-scan-input"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary" data-testid="store-scan-open-button">Open</Button>
        </form>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="store-requests-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["all", "Pending", "Issued", "Received", "Rejected"].map((s) => (
                <SelectItem key={s} value={s}>{s === "all" ? "All statuses" : s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" className="w-44" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="store-requests-date-filter" />
          {(statusFilter !== "all" || dateFrom) && (
            <Button variant="ghost" size="sm" onClick={() => { setStatusFilter("all"); setDateFrom(""); }} data-testid="store-requests-clear-filter">Clear</Button>
          )}
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={() => printMaterialList({
              title: `Material Requests — ${store} Store`,
              subtitle: `${statusFilter === "all" ? "All statuses" : statusFilter}${dateFrom ? ` · from ${fmtDay(dateFrom)}` : ""}`,
              entries: filtered.map(requestEntry),
            })}
            data-testid="store-requests-print-button"
          >
            <Printer className="h-4 w-4" /> Print List
          </Button>
        </div>
        <div className="lg:ml-auto grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-border bg-secondary/50 px-4 py-2" data-testid="store-total-issued">
            <p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Total Issued</p>
            <p className="font-mono font-extrabold">{fmtMoney(totals.issued)}</p>
          </div>
          <div className="rounded-lg border border-border bg-secondary/50 px-4 py-2" data-testid="store-total-received">
            <p className="text-[10px] uppercase font-mono tracking-widest text-muted-foreground">Total Received</p>
            <p className="font-mono font-extrabold text-emerald-700 dark:text-emerald-400">{fmtMoney(totals.received)}</p>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 mobile-compact" data-testid="store-requests-list">
        {filtered.length === 0 && <Card className="p-10 text-center text-muted-foreground sm:col-span-2 xl:col-span-3">No matching requests for {store}.</Card>}
        {filtered.map((r) => {
          const hasDiscrepancy = r.status === "Received" && r.items.some((it) => (it.received_qty || 0) !== (it.issued_qty || 0));
          return (
          <Card key={r.id} id={`req-${r.id}`} className={`p-4 fade-up flex flex-col ${highlightId === r.id ? "ring-2 ring-primary border-primary" : "border-orange-100 dark:border-slate-800"}`} data-testid="store-request-card">
            <div className="flex items-start justify-between mb-2">
              <div>
                <p className="font-semibold">{r.requested_by_name} <span className="font-mono text-[11px] text-muted-foreground" data-testid="store-request-code">#{requestCode(r.id)}</span></p>
                <p className="text-xs text-muted-foreground capitalize">{r.requester_role} · {r.requester_location || "—"}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                {r.required_by && <p className="text-xs font-semibold text-primary mt-0.5" data-testid="store-request-required-by">Required by {fmtDay(r.required_by)}</p>}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-7 w-7" title="Print this request" onClick={() => printMaterialList({ title: `Material Request — ${store} Store`, subtitle: (r.requester_location || "").trim(), entries: [requestEntry(r)] })} data-testid="store-request-print-button">
                  <Printer className="h-4 w-4" />
                </Button>
                <Badge variant="outline" className={statusCfg[r.status]}>{r.status}</Badge>
                {hasDiscrepancy && <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-300 text-[10px]" data-testid="store-discrepancy-badge">Short</Badge>}
              </div>
            </div>

            <div className="text-sm space-y-1 mb-3 flex-1">
              {r.status !== "Pending" && (
                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-1 border-b border-border">
                  <span>Material</span>
                  <span className="text-right">Req</span>
                  <span className="text-right">Issued</span>
                  <span className="text-right">Rcvd</span>
                </div>
              )}
              {r.items.map((it, i) => (
                r.status === "Pending" ? (
                  <div key={i} className="flex justify-between border-b border-dashed border-border/60 pb-0.5">
                    <span className="truncate">{it.name}</span>
                    <span className="font-mono font-semibold">{it.qty} {it.unit}</span>
                  </div>
                ) : (
                  <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center">
                    <span className="truncate">{it.name}</span>
                    <span className="font-mono text-muted-foreground text-right">{it.qty}</span>
                    <span className="font-mono font-semibold text-right">{it.issued_qty ?? 0}</span>
                    <span className="font-mono text-right">{r.status === "Received" ? (it.received_qty ?? 0) : "—"}</span>
                  </div>
                )
              ))}
            </div>

            {r.remarks && <p className="text-xs italic text-amber-700 dark:text-amber-400 mb-3">“{r.remarks}”</p>}
            <div className="mb-3">
              <HistoryStamps history={r.history} fallbackAction="Requested" fallbackName={r.requested_by_name} fallbackAt={r.created_at} testid="store-request-history" />
            </div>
            {r.status === "Pending" && (
              <div className="flex gap-2">
                <Button size="sm" className="flex-1 gap-1.5" onClick={() => openIssue(r)} data-testid="store-issue-button"><Check className="h-4 w-4" /> Issue</Button>
                <Button size="sm" variant="outline" className="flex-1 gap-1.5" onClick={() => reject(r.id)} data-testid="store-reject-button"><X className="h-4 w-4" /> Reject</Button>
              </div>
            )}
            {r.status === "Issued" && <p className="text-xs text-muted-foreground">Issued by {r.issued_by_name} · awaiting receipt</p>}
            {r.status === "Received" && <p className="text-xs text-emerald-600 font-medium">Received{r.received_by_name ? ` by ${r.received_by_name}` : ""}</p>}
            {r.status === "Rejected" && r.issued_by_name && <p className="text-xs text-muted-foreground">Rejected by {r.issued_by_name}</p>}
          </Card>
          );
        })}
      </div>

      {/* Partial issue dialog */}
      <Dialog open={!!issueReq} onOpenChange={(v) => !v && setIssueReq(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Issue Material — {issueReq?.requested_by_name}</DialogTitle>
            <DialogDescription>Enter how much of each material you are issuing. Stock is deducted only for issued quantities.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto">
            <div className="grid grid-cols-[1fr_auto_auto_auto] gap-2 text-[10px] uppercase font-mono tracking-wider text-muted-foreground pb-1 border-b border-border sticky top-0 bg-card">
              <span>Material</span>
              <span className="text-right">Requested</span>
              <span className="text-right">In Stock</span>
              <span className="text-right w-20">Issue Qty</span>
            </div>
            {issueReq?.items.map((it, i) => {
              const stock = stockOf(it.material_id);
              const over = Number(issueQty[it.material_id] || 0) > stock;
              return (
                <div key={i} className="grid grid-cols-[1fr_auto_auto_auto] gap-2 items-center py-1" data-testid="store-issue-item-row">
                  <span className="truncate text-sm">{it.name} <span className="text-muted-foreground">({it.unit})</span></span>
                  <span className="font-mono text-sm text-muted-foreground text-right">{it.qty}</span>
                  <span className={`font-mono text-sm text-right ${stock < it.qty ? "text-rose-600 font-semibold" : ""}`}>{stock}</span>
                  <Input
                    type="number" min="0" max={Math.min(it.qty, stock)} step="0.5"
                    className={`h-8 w-20 text-right font-mono ${over ? "border-rose-500" : ""}`}
                    data-testid="store-issue-qty-input"
                    value={issueQty[it.material_id] ?? ""}
                    onChange={(e) => setIssueQty({ ...issueQty, [it.material_id]: e.target.value })}
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button onClick={confirmIssue} disabled={busy} className="gap-2" data-testid="store-issue-confirm-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Confirm Issue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ---------------- Transfers ---------------- */
function Transfers({ store, fixed }) {
  const [fromStore, setFromStore] = useState(store);
  const toStore = fromStore === "MIHAN" ? "MANEWADA" : "MIHAN";
  const [mats, setMats] = useState([]);
  const [rows, setRows] = useState([{ material_id: "", qty: "" }]);
  const [remarks, setRemarks] = useState("");
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setFromStore(store); }, [store]);
  const loadMats = () => api.get("/materials", { params: { store: fromStore } }).then((r) => setMats(r.data)).catch(() => {});
  const loadHistory = () => api.get("/material-transfers").then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadMats(); }, [fromStore]);
  useEffect(() => { loadHistory(); }, [store]);

  const matById = useMemo(() => Object.fromEntries(mats.map((m) => [m.id, m])), [mats]);

  const lastTransfer = history.find((t) => t.from_store === fromStore);
  const repeatLast = () => {
    setRows(lastTransfer.items.map((it) => ({ material_id: it.material_id, qty: String(it.qty) })));
    setRemarks(lastTransfer.remarks || "");
    toast.success(`Filled from last transfer (${new Date(lastTransfer.created_at).toLocaleDateString("en-IN")})`);
  };

  const submit = async () => {
    const items = rows.filter((r) => r.material_id && Number(r.qty) > 0).map((r) => {
      const m = matById[r.material_id];
      return { material_id: m.id, name: m.name, unit: m.unit, qty: Number(r.qty) };
    });
    if (!items.length) return toast.error("Add at least one material");
    setSaving(true);
    try {
      await api.post("/material-transfers", { from_store: fromStore, to_store: toStore, items, remarks });
      toast.success(`Transferred to ${toStore}`);
      setRows([{ material_id: "", qty: "" }]); setRemarks(""); loadMats(); loadHistory();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 border-orange-100 dark:border-slate-800 h-fit">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-heading font-bold text-lg flex items-center gap-2"><ArrowRightLeft className="h-5 w-5 text-primary" /> Transfer Material</h3>
          <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={repeatLast} disabled={!lastTransfer} data-testid="transfer-repeat-last-button">
            <RotateCcw className="h-3.5 w-3.5" /> Repeat last transfer
          </Button>
        </div>
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 space-y-1">
            <Label className="text-xs">From</Label>
            <Select value={fromStore} onValueChange={setFromStore} disabled={fixed}>
              <SelectTrigger data-testid="transfer-from-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="MIHAN">MIHAN</SelectItem><SelectItem value="MANEWADA">MANEWADA</SelectItem></SelectContent>
            </Select>
          </div>
          <ArrowRightLeft className="h-5 w-5 mt-5 text-muted-foreground shrink-0" />
          <div className="flex-1 space-y-1">
            <Label className="text-xs">To</Label>
            <div className="h-10 flex items-center px-3 rounded-md border border-border bg-secondary font-semibold" data-testid="transfer-to-display">{toStore}</div>
          </div>
        </div>
        <div className="space-y-3">
          <Label>Materials</Label>
          {rows.map((row, idx) => {
            const m = matById[row.material_id];
            return (
              <div key={idx} className="flex flex-wrap sm:flex-nowrap items-end gap-2" data-testid="transfer-item-row">
                <div className="basis-full sm:basis-0 sm:flex-1 min-w-0">
                  <MaterialCombobox materials={mats} value={row.material_id} testid="transfer-material-combobox"
                    onChange={(v) => setRows(rows.map((r, i) => (i === idx ? { ...r, material_id: v } : r)))} />
                </div>
                <div className="w-20"><Input type="number" min="0" step="0.5" placeholder="Qty" data-testid="transfer-qty-input" value={row.qty}
                  onChange={(e) => setRows(rows.map((r, i) => (i === idx ? { ...r, qty: e.target.value } : r)))} /></div>
                <Badge variant="outline" className="h-10 px-2 min-w-[48px] justify-center text-xs">{m ? m.unit : "—"}</Badge>
                <Button variant="ghost" size="icon" onClick={() => setRows(rows.filter((_, i) => i !== idx))} disabled={rows.length === 1}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            );
          })}
          <Button variant="outline" size="sm" className="gap-2" data-testid="transfer-add-item-button" onClick={() => setRows([...rows, { material_id: "", qty: "" }])}><Plus className="h-4 w-4" /> Add material</Button>
          <div className="space-y-2"><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} data-testid="transfer-remarks" /></div>
          <Button onClick={submit} disabled={saving} className="gap-2 w-full" data-testid="transfer-submit-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRightLeft className="h-4 w-4" />} Transfer to {toStore}
          </Button>
        </div>
      </Card>

      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="font-heading font-bold text-lg">Transfer History</h3>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={!history.some((t) => t.to_store === store)}
            onClick={() => printMaterialList({
              title: `Transfers Received — ${store} Store`,
              subtitle: "Incoming from other stores",
              entries: history.filter((t) => t.to_store === store).map(transferEntry),
              issuedLabel: "Received",
            })}
            data-testid="transfer-print-received-button"
          >
            <Printer className="h-4 w-4" /> Print Received Transfers
          </Button>
        </div>
        <div className="space-y-3" data-testid="transfer-history-list">
          {history.length === 0 && <Card className="p-8 text-center text-muted-foreground">No transfers yet.</Card>}
          {history.map((t) => (
            <Card key={t.id} className="p-4 border-orange-100 dark:border-slate-800 fade-up">
              <div className="flex items-center justify-between mb-2">
                <div className="font-semibold flex items-center gap-2"><Badge variant="secondary">{t.from_store}</Badge><ArrowRightLeft className="h-4 w-4 text-primary" /><Badge variant="secondary">{t.to_store}</Badge></div>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</p>
                  <Button variant="ghost" size="icon" className="h-7 w-7" title="Print this transfer" onClick={() => printMaterialList({ title: `Transfer — ${t.from_store} to ${t.to_store}`, subtitle: t.to_store === store ? "Received" : "Sent", entries: [transferEntry(t)], issuedLabel: t.to_store === store ? "Received" : "Sent" })} data-testid="transfer-print-button">
                    <Printer className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <ul className="text-sm space-y-0.5">
                {t.items.map((it, i) => (<li key={i} className="flex justify-between"><span>{it.name}</span><span className="font-mono text-muted-foreground">{it.qty} {it.unit}</span></li>))}
              </ul>
              {t.remarks && <p className="text-xs italic text-muted-foreground border-t pt-2 mt-2">“{t.remarks}”</p>}
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
